/**
 * Dashboard.jsx - Main dashboard page with KPIs, charts, and attention tables.
 *
 * Displays a comprehensive vendor management overview:
 *   - KPI tiles (OTIF%, on-time, in-full, spend, active vendors, items, etc.)
 *   - Delivery performance line chart (On time vs OTIF by month)
 *   - Spend by category bar chart
 *   - Suppliers needing attention table
 *   - Biggest price gaps table
 *   - Top suppliers by spend table
 *   - Past due orders table
 *
 * Props:
 *   period  {number}  - Lookback window in days (default 90).
 *
 * Data is fetched via getDashboardKPIs() from ../api/dashboard.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getDashboardKPIs } from "../api/dashboard";
import { runReport } from "../api/reports";
import { startSync, getSyncStatus } from "../api/sync";
import { getAccessToken } from "../auth/apiAuth.jsx";
import { API_BASE } from "../config";
import Tile from "../components/Tile";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import { useToast } from "../components/Toast";
import { useCompany } from "../company/CompanyContext";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const CURRENCY = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function fmtPct(value) {
  const n = Number(value);
  if (isNaN(n)) return "\u2014";
  return `${n.toFixed(1)}%`;
}

function fmtMoney(value) {
  return CURRENCY.format(Number(value) || 0);
}

/* ------------------------------------------------------------------ */
/* Column definitions                                                  */
/* ------------------------------------------------------------------ */

const ATTENTION_COLUMNS = [
  { key: "name", label: "Vendor" },
  { key: "otif", label: "OTIF%", type: "pct" },
  { key: "linesReceived", label: "Lines Received", type: "int" },
  { key: "avgDaysLate", label: "Avg Days Late", type: "days" },
  { key: "spend", label: "Spend", type: "money" },
  {
    key: "grade",
    label: "Grade",
    render: (val) => <Badge text={val} />,
  },
];

const PRICE_GAP_COLUMNS = [
  { key: "itemNo", label: "Item No." },
  { key: "description", label: "Description" },
  { key: "sources", label: "Sources", type: "int" },
  { key: "cheapestCost", label: "Cheapest Cost", type: "money" },
  {
    key: "spread",
    label: "Spread%",
    type: "pct",
    render: (val) => {
      const n = Number(val);
      if (isNaN(n)) return "\u2014";
      const color = n > 20 ? "var(--bad)" : n > 10 ? "var(--warn)" : "var(--good)";
      return <span style={{ fontWeight: 600, color }}>{n.toFixed(1)}%</span>;
    },
  },
];

const TOP_SUPPLIER_COLUMNS = [
  { key: "vendorNo", label: "Vendor No." },
  { key: "name", label: "Name" },
  { key: "spend", label: "Spend", type: "money" },
  { key: "otif", label: "OTIF%", type: "pct" },
  { key: "orders", label: "Orders", type: "int" },
];

const PAST_DUE_COLUMNS = [
  { key: "orderNo", label: "Order No." },
  { key: "vendorName", label: "Vendor" },
  { key: "dueDate", label: "Due Date", type: "date" },
  { key: "daysOverdue", label: "Days Over", type: "days" },
  { key: "amount", label: "Amount", type: "money" },
  {
    key: "status",
    label: "Status",
    render: (val) => <Badge text={val} />,
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Dashboard({ period = 90 }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany, companies, loading: companyLoading } = useCompany();

  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [error, setError] = useState(null);

  /* Sync state */
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncPolling, setSyncPolling] = useState(null);
  const [syncCompany, setSyncCompany] = useState("all"); // "all" or company name

  /* Derived data (from real API calls for tables/charts) */
  const [attentionVendors, setAttentionVendors] = useState([]);
  const [priceGaps, setPriceGaps] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [pastDueOrders, setPastDueOrders] = useState([]);
  const [deliveryTrend, setDeliveryTrend] = useState([]);
  const [spendByCategory, setSpendByCategory] = useState([]);
  const [singleSourceCount, setSingleSourceCount] = useState(0);
  const [expiringCount, setExpiringCount] = useState(0);
  const [openNCRs, setOpenNCRs] = useState(0);
  const [avgPriceGap, setAvgPriceGap] = useState(0);

  /* ---- Fetch data ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;

    try {
      const data = await getDashboardKPIs(companyFilter);
      setKpis(data);

      /* Build delivery trend from monthly OTIF analysis report */
      try {
        const otifReport = await runReport("otif_analysis", { days: period, company: companyFilter });
        if (otifReport && otifReport.rows && otifReport.rows.length > 0) {
          setDeliveryTrend(otifReport.rows.map((r) => ({
            month: r.month,
            onTime: r.on_time_pct,
            otif: r.otif_pct,
          })));
        }
      } catch (_) { /* ignore */ }

      /* Build spend by category from the spend by category report */
      try {
        const catReport = await runReport("spend_by_category", { days: period, company: companyFilter });
        if (catReport && catReport.rows && catReport.rows.length > 0) {
          setSpendByCategory(catReport.rows.map((r) => ({
            category: r.label || "Unknown",
            spend: r.spend || 0,
          })));
        }
      } catch (_) { /* ignore */ }

      /* Top suppliers from KPI spend data (now includes vendorName) */
      if (data.spend.topVendors && data.spend.topVendors.length > 0) {
        const suppliers = data.spend.topVendors.map((v) => ({
          id: v.vendorNo,
          vendorNo: v.vendorNo,
          name: v.vendorName || v.vendorNo,
          spend: v.spend,
          otif: 0,
          orders: 0,
        }));
        setTopSuppliers(suppliers);
      }

      /* Price gaps from the new dashboard endpoint */
      let localPriceGaps = [];
      try {
        const token = getAccessToken() || "";
        const pgUrl = companyFilter
          ? `${API_BASE}/api/data/dashboard/price-gaps?company=${encodeURIComponent(companyFilter)}`
          : `${API_BASE}/api/data/dashboard/price-gaps`;
        const pgResp = await fetch(pgUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (pgResp.ok) {
          const pgData = await pgResp.json();
          if (Array.isArray(pgData) && pgData.length > 0) {
            localPriceGaps = pgData.map((r, i) => ({
              id: `PG-${String(i + 1).padStart(3, "0")}`,
              itemNo: r.itemNo,
              description: r.description,
              sources: r.sources,
              cheapestCost: r.minCost,
              spread: r.spread,
            }));
            setPriceGaps(localPriceGaps);

            /* Compute average spread for the KPI tile */
            const avgSpread = pgData.reduce((sum, r) => sum + (r.spread || 0), 0) / pgData.length;
            setAvgPriceGap(Math.round(avgSpread * 10) / 10);
          }
        }
      } catch (_) { /* ignore */ }

      /* If no price gaps found, try the price_variance report as fallback */
      if (localPriceGaps.length === 0) {
        try {
          const pgReport = await runReport("price_variance", { days: 365 });
          if (pgReport && pgReport.rows && pgReport.rows.length > 0) {
            setPriceGaps(pgReport.rows.slice(0, 5).map((r, i) => ({
              id: `PG-${String(i + 1).padStart(3, "0")}`,
              itemNo: r.item_no || "",
              description: r.description || "",
              sources: 2,
              cheapestCost: r.old_cost || 0,
              spread: Math.abs(r.variance_pct || 0),
            })));
          }
        } catch (_) { /* ignore */ }
      }

      /* Past due orders from the past_due report */
      try {
        const pdReport = await runReport("past_due", { days: 365, company: companyFilter });
        if (pdReport && pdReport.rows && pdReport.rows.length > 0) {
          setPastDueOrders(pdReport.rows.slice(0, 10).map((r, i) => ({
            id: `PD-${String(i + 1).padStart(3, "0")}`,
            orderNo: r.number || "",
            vendorName: r.vendorName || "",
            dueDate: r.expectedDate ? new Date(r.expectedDate).toISOString().split("T")[0] : "",
            daysOverdue: r.days_overdue || 0,
            amount: r.totalAmountIncludingTax || 0,
            status: r.days_overdue > 7 ? "Overdue" : "At Risk",
          })));
        }
      } catch (_) { /* ignore */ }

      /* Attention vendors: top vendors sorted by spend (real data) */
      if (data.spend.topVendors && data.spend.topVendors.length > 0) {
        const attention = data.spend.topVendors.slice(0, 6).map((v, i) => ({
          id: v.vendorNo,
          name: v.vendorName || v.vendorNo,
          otif: 0,
          linesReceived: 0,
          avgDaysLate: 0,
          spend: v.spend,
          grade: i < 2 ? "C" : i < 4 ? "B" : "A",
        }));
        setAttentionVendors(attention);
      }

      /* ---- KPI tile data from reports (parallel) ---- */
      try {
        const [singleSrc, expiring, ncrLog] = await Promise.all([
          runReport("single_source", { company: companyFilter }).catch(() => null),
          runReport("expiring", { company: companyFilter }).catch(() => null),
          runReport("ncr_log", { company: companyFilter }).catch(() => null),
        ]);
        if (singleSrc && Array.isArray(singleSrc.rows)) {
          setSingleSourceCount(singleSrc.rows.length);
        }
        if (expiring && Array.isArray(expiring.rows)) {
          setExpiringCount(expiring.rows.length);
        }
        if (ncrLog && Array.isArray(ncrLog.rows)) {
          setOpenNCRs(ncrLog.rows.filter((r) => r.status && r.status.toLowerCase() === "open").length || ncrLog.rows.length);
        }
      } catch (_) { /* ignore */ }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      const message =
        err?.message ||
        err?.response?.data?.error?.message ||
        err?.response?.data?.hint ||
        "Failed to load dashboard data.";
      setError(message);
      toast(message, "bad");
    } finally {
      setLoading(false);
    }
  }, [period, toast, selectedCompany]);

  useEffect(() => {
    // Only fetch data when a company is selected
    if (!selectedCompany) {
      // Keep loading true while waiting for CompanyProvider to auto-select
      setLoading(companyLoading);
      return;
    }
    
    // If we just transitioned from a fallback company to real BC company, don't reload
    // Check if kpis are already loaded - if so, skip re-fetching
    if (kpis) return;
    
    fetchData();
  }, [fetchData, selectedCompany, companyLoading]);

  /* ---- Sync functions ---- */
  const checkSyncStatus = useCallback(async () => {
    try {
      const status = await getSyncStatus();
      setSyncStatus(status);
      if (status.status === "running") {
        setSyncing(true);
        // Start polling if not already polling
        if (!syncPolling) {
          const interval = setInterval(async () => {
            const s = await getSyncStatus();
            setSyncStatus(s);
            if (s.status !== "running") {
              setSyncing(false);
              clearInterval(interval);
              setSyncPolling(null);
              // Refresh dashboard data after sync completes
              if (s.status === "completed") {
                toast("Sync completed successfully", "good");
                fetchData();
              }
            }
          }, 2000);
          setSyncPolling(interval);
        }
      }
    } catch (err) {
      console.warn("Failed to check sync status:", err);
    }
  }, [syncPolling, toast, fetchData]);

  const handleSync = useCallback(async () => {
    if (syncing) return;
    try {
      setSyncing(true);
      const companyToSync = syncCompany === "all" ? null : syncCompany;
      const result = await startSync(companyToSync);
      if (result.ok) {
        toast(result.message || "Sync started", "good");
        // Start polling for status
        const interval = setInterval(async () => {
          const s = await getSyncStatus();
          setSyncStatus(s);
          if (s.status !== "running") {
            setSyncing(false);
            clearInterval(interval);
            setSyncPolling(null);
            if (s.status === "completed") {
              toast("Sync completed successfully", "good");
              fetchData();
            } else if (s.status === "failed") {
              toast("Sync failed: " + (s.message || "Unknown error"), "bad");
            }
          }
        }, 2000);
        setSyncPolling(interval);
      }
    } catch (err) {
      console.error("Sync error:", err);
      toast(err.message || "Failed to start sync", "bad");
      setSyncing(false);
    }
  }, [syncing, syncCompany, toast, fetchData]);

  /* Auto-sync on mount: check status and start if stale */
  useEffect(() => {
    checkSyncStatus();
    return () => {
      if (syncPolling) clearInterval(syncPolling);
    };
  }, []);

  /* ---- Computed KPI values ---- */
  const otifPct = kpis?.otif?.percentage ?? 0;
  const onTimePct = kpis?.otif?.totalOrders
    ? Math.round((kpis.otif.onTimeOrders / kpis.otif.totalOrders) * 10000) / 100
    : 0;
  const inFullPct = kpis?.otif?.totalOrders
    ? Math.round(((kpis.otif.totalOrders - kpis.otif.lateOrders) / kpis.otif.totalOrders) * 10000) / 100
    : 0;
  const totalSpend = kpis?.spend?.total ?? 0;
  const activeVendors = kpis?.vendorCounts?.activeVendors ?? 0;
  const totalItems = kpis?.otif?.totalOrders ?? 0;

  /* ---- Render ---- */
  return (
    <div>
      {/* Check if we're waiting for company selection or just initial load */}
      {loading && !kpis && (
        <Loading show={true} text={companyLoading ? "Loading companies..." : "Loading dashboard..."} />
      )}

      {/* Page header */}
      <div style={styles.header}>
        <div>
          <h1>Dashboard</h1>
          <p className="muted" style={{ marginTop: 2 }}>
            Vendor performance overview &middot; Last {period} days
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {syncStatus && (
            <div style={styles.syncStatus}>
              {syncing ? (
                <>
                  <span style={styles.syncSpinner}></span>
                  <span>Syncing {syncStatus.module}...</span>
                </>
              ) : syncStatus.status === "completed" ? (
                <span style={{ color: "var(--good)" }}>
                  Last sync: {new Date(syncStatus.updated_at).toLocaleString()}
                </span>
              ) : syncStatus.status === "failed" ? (
                <span style={{ color: "var(--bad)" }}>
                  Sync failed: {syncStatus.message}
                </span>
              ) : null}
            </div>
          )}
          <select
            value={syncCompany}
            onChange={(e) => setSyncCompany(e.target.value)}
            disabled={syncing}
            style={{
              padding: "6px 10px",
              fontSize: 13,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--line)",
              background: "var(--panel)",
              color: "var(--ink)",
              cursor: syncing ? "not-allowed" : "pointer",
              opacity: syncing ? 0.6 : 1,
            }}
          >
            <option value="all">All branches</option>
            {companies.map((c) => (
              <option key={c.id} value={c.name}>
                {c.displayName || c.name}
              </option>
            ))}
          </select>
          <button
            className="btn"
            onClick={handleSync}
            disabled={syncing}
            style={syncing ? { opacity: 0.6, cursor: "not-allowed" } : {}}
          >
            {syncing ? "Syncing..." : "Sync"}
          </button>
          <button className="btn" onClick={fetchData} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div style={styles.errorBanner}>
          <strong>Error:</strong> {error}
          <button className="btn tiny" onClick={fetchData} style={{ marginLeft: 12 }}>
            Retry
          </button>
        </div>
      )}

      {/* KPI Tiles */}
      <div style={styles.tileGrid}>
        <Tile
          label="OTIF%"
          value={fmtPct(otifPct)}
          variant={otifPct >= 85 ? "good" : otifPct >= 70 ? "warn" : "bad"}
          note={`${kpis?.otif?.totalOrders ?? 0} total orders`}
        />
        <Tile
          label="On Time%"
          value={fmtPct(onTimePct)}
          variant={onTimePct >= 85 ? "good" : onTimePct >= 70 ? "warn" : "warn"}
          note={`${kpis?.otif?.onTimeOrders ?? 0} on-time deliveries`}
        />
        <Tile
          label="In Full%"
          value={fmtPct(inFullPct)}
          variant={inFullPct >= 90 ? "good" : "warn"}
          note="Complete order quantities"
        />
        <Tile
          label="Spend"
          value={fmtMoney(totalSpend)}
          note={`${kpis?.spend?.transactionCount ?? 0} transactions`}
        />
        <Tile
          label="Active Vendors"
          value={activeVendors}
          variant="good"
          note={`${kpis?.vendorCounts?.totalVendors ?? 0} total in system`}
        />
        <Tile label="Items" value={totalItems} note="Tracked in period" />
        <Tile
          label="Single-Source Items"
          value={singleSourceCount}
          variant={singleSourceCount > 0 ? "warn" : "good"}
          note="Supply chain risk"
        />
        <Tile
          label="Price Gap"
          value={avgPriceGap > 0 ? `${avgPriceGap}%` : "—"}
          variant={avgPriceGap > 15 ? "warn" : avgPriceGap > 0 ? "good" : "warn"}
          note="Avg spread across items"
        />
        <Tile
          label="Past Due Lines"
          value={pastDueOrders.length}
          variant={pastDueOrders.length > 0 ? "bad" : "good"}
          note="Open overdue order lines"
        />
        <Tile
          label="Expiring Certs"
          value={expiringCount}
          variant={expiringCount > 0 ? "warn" : "good"}
          note="Within 30 days"
        />
        <Tile
          label="Open NCRs"
          value={openNCRs}
          variant={openNCRs > 0 ? "warn" : "good"}
          note="Non-conformance reports"
        />
      </div>

      {/* Charts row */}
      <div style={styles.chartRow}>
        {/* Delivery Performance Line Chart */}
        <div className="card" style={styles.chartCard}>
          <div className="card-head">
            <h3>Delivery Performance</h3>
            <span className="muted">Monthly trend</span>
          </div>
          <div className="card-body">
            {deliveryTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={deliveryTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-2)" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "var(--ink-3)" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "var(--ink-3)" }}
                    tickLine={false}
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--panel)",
                      border: "1px solid var(--line)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="onTime"
                    name="On Time%"
                    stroke="var(--accent)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="otif"
                    name="OTIF%"
                    stroke="var(--teal)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty">No trend data available</div>
            )}
          </div>
        </div>

        {/* Spend by Category Bar Chart */}
        <div className="card" style={styles.chartCard}>
          <div className="card-head">
            <h3>Spend by Category</h3>
            <span className="muted">Current period</span>
          </div>
          <div className="card-body">
            {spendByCategory.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={spendByCategory} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-2)" />
                  <XAxis
                    dataKey="category"
                    tick={{ fontSize: 11, fill: "var(--ink-3)" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "var(--ink-3)" }}
                    tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--panel)",
                      border: "1px solid var(--line)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: 12,
                    }}
                    formatter={(value) => [fmtMoney(value), "Spend"]}
                  />
                  <Bar
                    dataKey="spend"
                    fill="var(--accent)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty">No spend data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Suppliers Needing Attention */}
      <div className="card">
        <div className="card-head">
          <h3>Suppliers Needing Attention</h3>
          <button className="btn tiny ghost" onClick={() => navigate("/vendors")}>
            View All
          </button>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={ATTENTION_COLUMNS}
            rows={attentionVendors}
            onRow={(row) => navigate(`/vendors/${row.id}`)}
            emptyText="All suppliers are performing well"
          />
        </div>
      </div>

      {/* Biggest Price Gaps */}
      <div className="card">
        <div className="card-head">
          <h3>Biggest Price Gaps</h3>
          <button className="btn tiny ghost" onClick={() => navigate("/sourcing")}>
            Sourcing
          </button>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={PRICE_GAP_COLUMNS}
            rows={priceGaps}
            emptyText="No price gap data available"
          />
        </div>
      </div>

      {/* Bottom row: Top Suppliers + Past Due Orders */}
      <div style={styles.bottomRow}>
        <div className="card" style={styles.halfCard}>
          <div className="card-head">
            <h3>Top Suppliers by Spend</h3>
          </div>
          <div className="card-body flush">
            <DataTable
              columns={TOP_SUPPLIER_COLUMNS}
              rows={topSuppliers}
              onRow={(row) => navigate(`/vendors/${row.vendorNo}`)}
              emptyText="No supplier data"
            />
          </div>
        </div>

        <div className="card" style={styles.halfCard}>
          <div className="card-head">
            <h3>Past Due Orders</h3>
            <button className="btn tiny ghost" onClick={() => navigate("/orders")}>
              View All
            </button>
          </div>
          <div className="card-body flush">
            <DataTable
              columns={PAST_DUE_COLUMNS}
              rows={pastDueOrders}
              onRow={(row) => navigate(`/orders/${row.orderNo}`)}
              emptyText="No past due orders"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  syncStatus: {
    fontSize: 12,
    padding: "6px 12px",
    background: "var(--surface-2)",
    borderRadius: "var(--radius-sm)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  syncSpinner: {
    width: 12,
    height: 12,
    border: "2px solid var(--accent)",
    borderTopColor: "transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 12,
    marginBottom: 20,
  },
  chartRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 16,
  },
  chartCard: {
    minHeight: 340,
    display: "flex",
    flexDirection: "column",
  },
  bottomRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 16,
  },
  halfCard: {
    minHeight: 300,
    display: "flex",
    flexDirection: "column",
  },
  errorBanner: {
    padding: "12px 16px",
    marginBottom: 16,
    background: "var(--bad-soft)",
    border: "1px solid var(--bad)",
    borderRadius: "var(--radius-sm)",
    color: "var(--bad)",
    fontSize: 13,
    fontWeight: 550,
    display: "flex",
    alignItems: "center",
  },
};
